<template>
  <section class="table-container">
    <div :class="['table-head']">
      <TableRow
        :values="headers"
        @checkboxClick="handleHeaderClick"
        :isSelect="isSelect"
        :hasNested="isNested"
        style="background-color:unset;"
        @cellClick="handleSortClick"
        :isIgnored="isPlaceholder"
        :isHidingId="isHidingId"
        :isHeader="true"
        :editOptions="editOptions"
        :style="{ 'font-size': headerFontSize }"
        :columnWidth="columnWidth"
        :buttonText="buttonText"
        :customRowGridStr="customRowGridStr"
        :extraActions="extraActions.map(() => {})"
      />
    </div>

    <div class="table-body" v-if="paginatedTableData">
      <template v-for="(item, index) in paginatedTableData">
        <TableRow
          v-if="!rowData(item, currentStartIndex + index).includes('placeholder')"
          :key="tableRowKey(item, currentStartIndex + index)"
          :showArrow="isNestedTable(item, currentStartIndex + index)"
          :values="rowData(item, currentStartIndex + index)"
          :rowIndex="currentStartIndex + index"
          :isHidingRowIndex="isHidingRowIndex"
          :checkboxValue="selectAll"
          @checkboxClick="checkboxHandler"
          :isSelect="isSelect"
          :hasNested="isNested"
          @openNested="openNested"
          :isHidingId="isHidingId"
          :editOptions="editOptions"
          @editRow="emitRowToEdit"
          @removeRow="emitRowToRemove"
          @rowClick="rowClick"
          :style="{ 'font-size': bodyFontSize }"
          :columnWidth="columnWidth"
          :buttonText="buttonText"
          @buttonClick="buttonClicked"
          @updateTable="updateTable"
          @setActionFlags="setActionFlags"
          @toggleFlag="toggleFlag"
          :extraActions="extraActions"
          :customRowGridStr="customRowGridStr"
          :extraClass="rowClass ? rowClass(item) : ''"
          :nestedTableOpen="activeNestedIndexes.includes(currentStartIndex + index)"
          @cellClick="clickedCell"
          @action="
            (type) => {
              handleActionClick({ type, idx: currentStartIndex + index });
            }
          "
        />
        <Table
          :key="currentStartIndex + index"
          v-if="activeNestedIndexes.includes(currentStartIndex + index)"
          :tableData="getNestedTableData(index)"
          :buttonText="nestedButtonText"
          :idField="nestedIdField"
          @removeRow="removeNestedRow($event, currentStartIndex + index)"
          :editOptions="{ edit: isAdmin && isNestedEditable, remove: isAdmin }"
          @buttonClick="nestedButtonClicked($event, currentStartIndex + index)"
        />
      </template>
    </div>
    <div class="pagination-container" v-if="isPaginated && totalPages > 1">
      <div v-if="!isSeeAll" class="pagination-controls">
        <div class="pagination-progress">
          <button @click="prevPage" :disabled="currentPage === 1">{{ $t('previous')  }}</button>
          <div class="progress-bar">
            <span
              v-for="page in visiblePages"
              :key="page"
              :class="{ active: currentPage === page }"
              @click="setPage(page)"
            >
              {{ page }}
            </span>
          </div>
          <button @click="nextPage" :disabled="currentPage === totalPages">{{ $t('next') }}</button>
        </div>
      </div>
      <div class="see-all-toggle">
        <button @click="toggleSeeAll">
          {{ isSeeAll ? $t('showPaginated') : $t('seeAll') }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import TableRow from './TableRow';

export default {
  name: 'Table',
  props: {
    tableData: { type: Array, required: true },
    idField: { type: String, required: false },
    nestedIdField: { type: String, required: false },
    headerFontSize: { type: String, required: false },
    bodyFontSize: { type: String, required: false },
    columnWidth: { type: String, required: false },
    buttonText: { type: String, required: false },
    nestedButtonText: { type: String, required: false },
    isSelect: { type: Boolean, default: false },
    isNested: { type: Boolean, default: false },
    isNestedEditable: { type: Boolean, default: true },
    isHidingId: { type: Boolean, default: true },
    isAdmin: { type: Boolean, default: false },
    customRowGridStr: { type: String, required: false },
    initialOpenNested: { type: Array, default: () => [] },
    editOptions: {
      type: Object,
      default: () => ({ edit: false, remove: false }),
    },
    extraActions: { type: Array, default: () => [] },
    isHidingRowIndex: { type: Boolean, default: true },
    isPaginated: { type: Boolean, default: false, required: false },
    itemsPerPage: { type: Number, default: 10, required: false },
    initialPage: { type: Number, default: 1, required: false },
    isSeeAllProp: { type: Boolean, default: false, required: false },
    // { [columnKey]: (rowA, rowB) => number } - overrides the default sort for that column
    sortComparators: { type: Object, default: () => ({}), required: false },
    rowClass: { type: Function, required: false },
  },
  data() {
    return {
      headers: [],
      tableDataCopy: [],
      haveNestedTable: [],
      selectedItemsIndexes: [],
      activeNestedIndexes: [],
      selectAll: false,
      currentPage: this.initialPage,
      isSeeAll: this.isSeeAllProp,
    };
  },
  created() {
    if (this.initialOpenNested) this.activeNestedIndexes = this.initialOpenNested;
    this.updateTable();
    if (!this.tableData[0]) return;
    const tableItem = JSON.parse(JSON.stringify(this.tableData[0]));
    const tableKeys = Object.keys(tableItem);
    if (this.idField) {
      delete tableItem[this.idField];
    }
    this.handleSortClick(tableKeys[0], true);
  },
  computed: {
    totalPages() {
      return Math.ceil(this.tableData.length / this.itemsPerPage);
    },
    currentStartIndex() {
      // When showing the full list, v-for indexes are already absolute.
      // Keep page offset only for paginated slices so actions (edit/remove) hit the right row.
      if (this.isSeeAll) return 0;
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    paginatedTableData() {
      const filteredData = this.tableDataCopy
      if (!this.isPaginated || this.isSeeAll) return filteredData;
      const startIndex = this.currentStartIndex;
      const endIndex = startIndex + this.itemsPerPage;
      return filteredData.slice(startIndex, endIndex);
    },
    columnCount() {
      return this.headers.length;
    },
    selectedItems() {
      return this.selectedItemsIndexes.map((index) => this.tableData[index]);
    },
    isPlaceholder() {
      const parent = this?.$parent?.$el?.className;
      return this.tableData?.[0]?.[parent === 'shift-table' ? 'shift name' : 'name']?.includes('placeholder')
        ? true
        : false;
    },
    visiblePages() {
      const maxVisible = 5; // Number of pages to show in the bar
      const start = Math.max(this.currentPage - Math.floor(maxVisible / 2), 1);
      const end = Math.min(start + maxVisible - 1, this.totalPages);

      // Adjust start if end is near the last page
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  methods: {
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
      this.closeAllNestedTables()
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
      this.closeAllNestedTables()
    },
    handleActionClick(actionType) {
      this.$emit('action', actionType);
    },
    tableRowKey(item, index) {
      return item?.device_id || item?.id ? item?.device_id || item?.id : index + 100 + index * 2;
    },
    rowClick(index) {
      this.$emit('rowClick', this.tableData[index][this.idField]);
    },
    clickedCell(payload) {
      this.$emit('cellClicked', payload);
    },
    emitRowToEdit(index) {
      this.$emit('editRow', this.tableData[index], this.currentPage);
    },
    emitRowToRemove(index) {
      const pageNumToShow = this.currentPage === this.totalPages && this.tableDataCopy.length % this.itemsPerPage === 1 ? this.currentPage - 1 : this.currentPage;
      this.$emit('removeRow', this.tableData[index][this.idField], pageNumToShow);
    },
    buttonClicked(index) {
      this.$emit('buttonClick', index);
    },
    nestedButtonClicked(nestedIndex, roomIndex) {
      this.$emit('nestedButtonClick', nestedIndex, roomIndex);
    },
    removeNestedRow(id, index) {
      this.$emit('deleteNestedItem', id, this.tableData[index][this.idField], this.currentPage);
    },
    handleSortClick(value, isActive) {
      value = value.split(' ').join('_')
      this.closeAllNestedTables()
      
      if (isActive) {
          // Get the current slice of data based on pagination
          const startIndex = this.currentStartIndex
          const endIndex = startIndex + this.itemsPerPage
          let currentPageData = []
          let originalPageData = []
          if (this.isPaginated && !this.isSeeAll) {
            currentPageData = this.tableDataCopy.slice(startIndex, endIndex)
            originalPageData = this.tableData.slice(startIndex, endIndex)
          } else {
            currentPageData = this.tableDataCopy
            originalPageData = this.tableData
          }

          // Sort the current page data
          const itemsToSort = [currentPageData, originalPageData];
          const customComparator = this.sortComparators[value]
          itemsToSort.forEach((keyToSort) => keyToSort.sort((a, b) => {
            if (customComparator) return customComparator(a, b)
            const aValue = a[value]
            const bValue = b[value]
            
            if ((aValue && bValue) || (aValue === 0 && bValue) || (aValue && bValue === 0) || (aValue === 0 && bValue === 0)) {
              if (typeof aValue === "string" && typeof bValue === "string") {
                return aValue.localeCompare(bValue); // Proper string comparison
              } else if (typeof aValue === "number" && typeof bValue === "number") {
                return aValue - bValue; // Numeric comparison
              } else if (typeof aValue === 'object' && aValue.date) {
                return new Date(aValue.date) - new Date(bValue.date);
              } else if (typeof aValue === 'object' && aValue.icon) {
                if (value === 'counter_status' || value === 'status') {
                  return aValue.icon.localeCompare(bValue.icon)
                } else {
                  return bValue.icon.localeCompare(aValue.icon)
                }
              }
            }
            return 0
          }))
          this.tableDataCopy.splice(startIndex, currentPageData.length, ...currentPageData);
          this.tableData.splice(startIndex, originalPageData.length, ...originalPageData);
      } else {
        const tableItem = JSON.parse(JSON.stringify(this.tableData[0]));
        const tableKeys = Object.keys(tableItem);
        if (this.idField) {
          delete tableItem[this.idField];
        }
        this.handleSortClick(tableKeys[0], true);
      }
      this.updateTable()
    },
    openNested(index, isOpen) {
      if (!isOpen) {
        this.activeNestedIndexes.push(index);
      } else {
        this.activeNestedIndexes = this.activeNestedIndexes.filter((item) => item !== index);
      }
      this.$emit('updatedNestedOpen', JSON.parse(JSON.stringify(this.activeNestedIndexes)));
    },
    checkboxHandler(value, index) {
      if (value) {
        this.selectedItemsIndexes.push(index);
      } else {
        const indexToRemove = this.selectedItemsIndexes.indexOf(index);
        this.selectedItemsIndexes.splice(indexToRemove, 1);
      }
      this.$emit('select', this.selectedItems);
    },
    rowData(item, index) {
      if (item.nestedTable) {
        this.haveNestedTable.push(index);
        delete item.nestedTable;
      }
      if (this.idField && this.isHidingId) delete item[this.idField];
      return Object.values(item);
    },
    updateTable() {
      if (!this.tableData || !this.tableData[0]) return;
      const firstItem = this.tableData[0];
      let itemForValues = {};
      if (firstItem.nestedTable || this.idField) {
        itemForValues = { ...firstItem };
        delete itemForValues.nestedTable;
        if (this.isHidingId) {
          delete itemForValues[this.idField];
        }
      } else {
        itemForValues = firstItem;
      }
      const headers = Object.keys(itemForValues).map((k) => (k === 'is_template' ? '' : k));
      
      this.tableDataCopy = JSON.parse(JSON.stringify(this.tableData));
      if (!this.isHidingRowIndex) {
        headers.push('#');
      }
      this.headers = headers;
    },
    handleHeaderClick(value) {
      this.selectAll = value;
    },
    setActionFlags(actionFlags) {
      this.$emit('setActionFlags', actionFlags);
    },
    toggleFlag(flag, actionFlags) {
      this.$emit('toggleFlag', flag, actionFlags);
    },
    isNestedTable(item, index) {
      return this.haveNestedTable.includes(index);
    },
    getNestedTableData(index) {
      const adjustedIndex = this.currentStartIndex + index;
      return JSON.parse(JSON.stringify(this.tableData[adjustedIndex].nestedTable));
    },
    setPage(page) {
      this.currentPage = page;
      this.closeAllNestedTables(); // Optional: Close nested tables
    },
    closeAllNestedTables() {
      this.activeNestedIndexes = [];
    },
    toggleSeeAll() {
      this.isSeeAll = !this.isSeeAll;
      const mainContainer = document.querySelector('.main');
      if (mainContainer.classList.contains('overflow-hidden')) {
        mainContainer.classList.remove('overflow-hidden');
      }
      this.closeAllNestedTables(); // Optional: Close nested tables
      this.$emit('toggleSeeAll', this.isSeeAll)
    },
  },
  watch: {
    tableData() {
      this.updateTable();
    },
    initialPage(page) {
      if (page >= 1 && page !== this.currentPage) {
        this.currentPage = page;
      }
    },
    currentPage(page) {
      this.$emit('pageChange', page);
    },
  },
  components: {
    TableRow,
    Table: this,
  },
};
</script>

<style lang="scss">
@use '@/styles/vars.scss' as *;
@use '@/styles/mixins.scss' as *;

$head-color: #909090;

.table-container {
  background-color: #fff;
  border-collapse: collapse;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  margin-inline-start: 22px;
  padding: 0 10px;
}

.table-head {
  text-transform: uppercase;
  color: $head-color;
  font-size: $font-size-smaller;
}

.table-body {
  font-size: $font-size-small;
}

.pagination-container {
  @include flex-center;

  .pagination-controls {
    @include flex-center;
    flex: 1;
    gap: 10px;
    padding: 10px;

    button {
      background-color: #007bff;
      color: #fff;
      border: none;
      border-radius: 20px;
      cursor: pointer;
      width: auto;
      padding: 0 15px;
      font-size: 14px;
      font-weight: bold;
      width: 80px;
      height: 35px;

      &:hover {
        background-color: #0056b3;
      }

      &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
      }
    }
    .pagination-progress {
      @include flex-center;
      gap: 10px;
      .progress-bar {
        display: flex;
        gap: 5px;
        overflow-x: auto;
      }
      span {
        width: 30px;
        height: 30px;
        line-height: 30px;
        text-align: center;
        border-radius: 50%;
        background-color: #ccc;
        cursor: pointer;

        &.active {
          background-color: #007bff;
          color: #fff;
        }

        &:hover {
          background-color: #0056b3;
          color: white;
        }

        transition: background-color 0.3s ease, color 0.3s ease;
      }
    }
  }

  .see-all-toggle {
    padding: 10px;
    margin-left: auto;

    button {
      background-color: #007bff;
      color: #fff;
      border: none;
      border-radius: 20px;
      cursor: pointer;
      padding: 0 15px;
      font-size: 14px;
      font-weight: bold;
      height: 35px;

      &:hover {
        background-color: #0056b3;
      }

      transition: background-color 0.3s ease;
    }
  }
}

</style>
